import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import { Avatar, EditUserButton, Money, OnlineStatus } from "../../components";
import { Button } from "../../components/ui";
import { getBaseCurrency } from "../../core/currency";
import useUserUpdates from "../../hooks/useUserUpdates";

// Exported — Fontaine M55A's DataTable-based directory columns (Table.jsx) reuse these two
// exactly rather than redefine them, so the picker path here and the directory result surface
// can never drift on what "Active" or a display name means.
export const statusMeta = (contact) => {
  if (contact.deleted_at) {
    return { label: "Blocked", tone: "blocked" };
  }
  if (contact.email_verified_at) {
    return { label: "Active", tone: "active" };
  }
  if (contact.invitation_email_sent_at) {
    return { label: "Invited", tone: "invited" };
  }
  return { label: "Pending", tone: "pending" };
};

export const displayName = (contact) => {
  if (contact.first_name || contact.last_name) {
    return `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
  }
  return contact.email || `User #${contact.id}`;
};

const ContactRow = ({
  item,
  onSelectClick,
  onOpenContact,
  selectedContactId,
  pickerLayout,
}) => {
  const { type } = useParams();
  const [contact, setContact] = useState(item);

  useEffect(() => setContact(item), [item]);

  // A row was a frozen snapshot of the search hit, so editing a user in the
  // edit tab left a stale name/email in the picker until the list remounted.
  // Storage-event only — a focus refetch here would fire one request per row.
  const onUserRefreshed = useCallback(
    (fresh) => setContact((prev) => ({ ...prev, ...fresh })),
    []
  );
  useUserUpdates(contact?.id, onUserRefreshed);

  const isSelected = selectedContactId != null && selectedContactId === contact?.id;
  const status = statusMeta(contact);
  const name = displayName(contact);
  const created = contact.created_at
    ? dayjs(contact.created_at).format("DD MMM YYYY")
    : "—";

  const onClick = (e) => {
    e.preventDefault();
    if (onSelectClick) return;
    if (onOpenContact) onOpenContact(contact);
  };

  if (!contact) return null;

  const AvatarBlock = (
    <div className="users-row__avatar avatar-with-status">
      <Avatar
        fileId={contact?.avatar_file_id}
        onClick={onClick}
        fallback="/assets/default-avatar-dark.svg"
      />
      <OnlineStatus lastActiveDate={contact?.user?.last_active_at} />
    </div>
  );

  // Compact picker used by Assign Client / Lookbook flows
  if (onSelectClick) {
    if (pickerLayout === "horizontal") {
      return (
        <>
          <tr className={`mobile${isSelected ? " selected" : ""}`}>
            <td>
              <div className="users-row-card users-row-card--assign-picker">
                {AvatarBlock}
                <div className="users-row-card__copy">
                  <div className="users-row__name">{name}</div>
                  <div className="users-row__muted">
                    {contact.email || contact.phone || "—"}
                  </div>
                </div>
                <div className="users-row__actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    data-btn="client-select-confirm"
                    onClick={() => onSelectClick(contact)}
                  >
                    Select
                  </Button>
                  <EditUserButton user={contact} className="users-row__edit" />
                </div>
              </div>
            </td>
          </tr>
          <tr className={`desktop${isSelected ? " selected" : ""}`}>
            <td className="col-user">
              <div className="users-row__user">
                {AvatarBlock}
                <span className="users-row__name">{name}</span>
              </div>
            </td>
            <td className="col-contact">{contact.email || contact.phone || "—"}</td>
            <td className="col-action">
              <div className="users-row__actions">
                <Button
                  variant="secondary"
                  size="sm"
                  data-btn="client-select-confirm"
                  onClick={() => onSelectClick(contact)}
                >
                  Select
                </Button>
                <EditUserButton user={contact} className="users-row__edit" />
              </div>
            </td>
          </tr>
        </>
      );
    }

    return (
      <>
        <tr className={`mobile${isSelected ? " selected" : ""}`}>
          <td>
            <div className="users-row-card">
              <div className="users-row-card__main">
                {AvatarBlock}
                <div>
                  <div className="users-row__name">{name}</div>
                  <div className="users-row__muted">{contact.email || contact.phone || "—"}</div>
                </div>
              </div>
              <div className="users-row__actions">
                <Button
                  variant="secondary"
                  size="sm"
                  data-btn="client-select-confirm"
                  onClick={() => onSelectClick(contact)}
                >
                  Select
                </Button>
                <EditUserButton user={contact} className="users-row__edit" />
              </div>
            </div>
          </td>
        </tr>
        <tr className={`desktop${isSelected ? " selected" : ""}`}>
          <td className="col-user">
            <div className="users-row__user">
              {AvatarBlock}
              <span className="users-row__name">{name}</span>
            </div>
          </td>
          <td className="col-contact">{contact.email || contact.phone || "—"}</td>
          <td className="col-action">
            <div className="users-row__actions">
              <Button
                variant="secondary"
                size="sm"
                data-btn="client-select-confirm"
                onClick={() => onSelectClick(contact)}
              >
                Select
              </Button>
              <EditUserButton user={contact} className="users-row__edit" />
            </div>
          </td>
        </tr>
      </>
    );
  }

  return (
    <>
      <tr className={`mobile${isSelected ? " selected" : ""}`} onClick={onClick}>
        <td>
          <div className="users-row-card">
            <div className="users-row-card__main">
              {AvatarBlock}
              <div className="users-row-card__copy">
                <div className="users-row__name">{name}</div>
                <div className="users-row__muted">{contact.email || contact.phone || `ID ${contact.id}`}</div>
              </div>
              <span className={`users-status users-status--${status.tone}`}>
                <span className="users-status__dot" />
                {status.label}
              </span>
            </div>
            <div className="users-row-card__meta">
              <span>
                <label>Turnover</label>
                {/* TODO(currency): turnover is a cross-record aggregate with no
                per-record currency — deliberate store-base render. */}
                <Money amount={contact.turnover} currency={getBaseCurrency()} zero="—" />
              </span>
              <span>
                <label>Created</label>
                {created}
              </span>
            </div>
          </div>
        </td>
      </tr>

      <tr
        className={`desktop${isSelected ? " selected" : ""}`}
        onClick={onClick}
      >
        <td className="col-user">
          <div className="users-row__user">
            {AvatarBlock}
            <div className="users-row__identity">
              <span className="users-row__name">{name}</span>
              <span className="users-row__muted">ID {contact.id}</span>
            </div>
          </div>
        </td>

        <td className="col-contact">
          <div className="users-row__contact">
            <span>{contact.email || "—"}</span>
            {contact.phone && <span className="users-row__muted">{contact.phone}</span>}
          </div>
        </td>

        {(type === "merchant" || type === "supplier") && (
          <td className="col-store">
            <div className="users-row__contact">
              <span>{contact.store?.name || "—"}</span>
              <span className="users-row__muted">{contact.store_id || "—"}</span>
            </div>
          </td>
        )}

        {type === "expert" && (
          <td className="col-role">
            <div className="users-row__contact">
              <span>{contact.title || "—"}</span>
              <span className="users-row__muted">{contact.company || "—"}</span>
            </div>
          </td>
        )}

        <td className="col-status">
          <span className={`users-status users-status--${status.tone}`}>
            <span className="users-status__dot" />
            {status.label}
          </span>
        </td>

        {type !== "merchant" && type !== "supplier" && (
          <td className="col-turnover">
            {/* TODO(currency): turnover is a cross-record aggregate with no
                per-record currency — deliberate store-base render. */}
                <Money amount={contact.turnover} currency={getBaseCurrency()} zero="—" />
          </td>
        )}

        <td className="col-created">{created}</td>

        <td className="col-action" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="users-row__more"
            aria-label="Open profile"
            onClick={onClick}
          >
            ⋮
          </button>
        </td>
      </tr>
    </>
  );
};

export default ContactRow;
